import { useState, useRef, useCallback } from 'react';
import { Row, Col, Button, Form, Spinner, Container, Modal } from 'react-bootstrap';
import { useScanNetworkMutation, useStopNetworkMutation, useGetSitesQuery } from '../slices/sitesApiSlice';
import { useCreateDeviceMutation } from '../slices/devicesApiSlice';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const NetworkScanScreen = () => {
    const { data: sites, isLoading: isSitesLoading } = useGetSitesQuery();
    const [subnet, setSubnet] = useState('');
    const [mask, setMask] = useState('24');
    const [hosts, setHosts] = useState([]);
    
    // Add Device Modal State
    const [showAddModal, setShowAddModal] = useState(false);
    const [hostToAdd, setHostToAdd] = useState(null);
    const [targetSiteId, setTargetSiteId] = useState('');

    const [scanNetwork, { isLoading }] = useScanNetworkMutation();
    const [stopNetwork, { isLoading: isStopping }] = useStopNetworkMutation();
    const [createDevice, { isLoading: isAddingDevice }] = useCreateDeviceMutation();
    const gridRef = useRef();

    const handleScan = async (e) => {
        e.preventDefault();
        if (!subnet) {
            toast.error("Please enter a subnet IP address.");
            return;
        }

        // Clean up inputs and combine
        const cleanSubnet = subnet.trim().replace(/[^a-zA-Z0-9.-]/g, '');
        const cleanMask = mask.trim().replace(/[^0-9]/g, '');
        const target = cleanMask ? `${cleanSubnet}/${cleanMask}` : cleanSubnet;

        try {
            const res = await scanNetwork({ target }).unwrap();
            setHosts(res.hosts || []);
            toast.success(`Scan completed. Found ${res.hosts?.length || 0} active hosts.`);
        } catch (err) {
            toast.error(err?.data?.message || err.error || "Failed to execute scan");
        }
    };

    const handleStopScan = async () => {
        try {
            const res = await stopNetwork().unwrap();
            toast.success(res.message || "Scan stopped.");
        } catch (err) {
            toast.error(err?.data?.message || err.error || "Failed to stop scan");
        }
    };

    const handleAddClick = (host) => {
        setHostToAdd(host);
        if (sites && sites.length > 0) {
            setTargetSiteId(sites[0]._id);
        } else {
            setTargetSiteId('');
        }
        setShowAddModal(true);
    };

    const handleConfirmAddDevice = async () => {
        if (!targetSiteId) {
            toast.error("Please select a target site.");
            return;
        }
        if (!hostToAdd) return;

        const selectedSite = sites.find(s => s._id === targetSiteId);

        const newDeviceData = {
            name: hostToAdd.hostname || hostToAdd.ip,
            type: 'Workstation',
            hostname: hostToAdd.hostname || '',
            serial: '',
            firmware: hostToAdd.os !== 'Unknown' ? hostToAdd.os : '',
            mac: hostToAdd.mac || '',
            lanIP: hostToAdd.ip,
            wanIP: '',
            companyName: selectedSite?.company || '',
            vendor: hostToAdd.vendor || '',
            supportNumber: '',
            connType: 'Ethernet',
            location: 'Discovered via Network Scan',
            site: targetSiteId
        };

        try {
            const res = await createDevice(newDeviceData).unwrap();
            toast.success(`Successfully added ${res.addedDevice.name} to ${selectedSite?.name || 'Site'}.`);
            setShowAddModal(false);
            setHostToAdd(null);
        } catch (err) {
            toast.error(err?.data?.message || err.error || "Failed to add device");
        }
    };

    const onGridReady = (params) => {
        gridRef.current = params.api;
        params.api.sizeColumnsToFit();
    };

    const onExportClick = useCallback(() => {
        if (!gridRef.current) return;
        const fileName = `network_scan_${Date.now()}.csv`;
        gridRef.current.exportDataAsCsv({ fileName });
    }, []);

    const columnDefs = [
        { 
            headerName: 'IP Address', 
            field: 'ip', 
            sortable: true, 
            filter: true, 
            width: 140,
            cellRenderer: (params) => {
                const ip = params.value;
                return (
                    <a href={`http://${ip}`} target="_blank" rel="noopener noreferrer" style={{ color: '#00cec9', textDecoration: 'none' }}>
                        {ip}
                    </a>
                );
            }
        },
        { headerName: 'Hostname', field: 'hostname', sortable: true, filter: true, flex: 2, minWidth: 180 },
        { headerName: 'MAC Address', field: 'mac', sortable: true, filter: true, width: 160 },
        { headerName: 'Vendor', field: 'vendor', sortable: true, filter: true, flex: 1, minWidth: 150 },
        {
            headerName: 'Actions',
            field: 'actions',
            width: 140,
            cellRenderer: (params) => {
                const host = params.data;
                return (
                    <Button 
                        size="sm" 
                        variant="success" 
                        style={{ padding: '2px 8px', fontSize: '12px' }}
                        onClick={() => handleAddClick(host)}
                        disabled={isAddingDevice}
                    >
                        + Add to Site
                    </Button>
                );
            }
        }
    ];

    return (
        <Container className="my-4">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Outfit:wght@300;400;500;600;700;800&display=swap');

                .network-scan-container {
                    color: #ffffff;
                    font-family: 'Outfit', 'Inter', sans-serif;
                }
                .scan-title {
                    font-size: 32px;
                    font-weight: 700;
                    background: linear-gradient(135deg, #ffffff 40%, #00cec9 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    margin-bottom: 24px;
                }
                .scan-card {
                    background: rgba(30, 39, 46, 0.4) !important;
                    border: 1px solid rgba(255, 255, 255, 0.05) !important;
                    border-radius: 12px !important;
                    padding: 24px;
                    margin-bottom: 24px;
                }
                .results-header {
                    font-size: 18px;
                    font-weight: 700;
                    color: #00cec9;
                    margin-bottom: 16px;
                }
                .ag-theme-alpine {
                    --ag-background-color: rgba(30, 39, 46, 0.6) !important;
                    --ag-header-background-color: rgba(45, 52, 54, 0.8) !important;
                    --ag-odd-row-background-color: rgba(255, 255, 255, 0.02) !important;
                    --ag-row-hover-color: rgba(13, 148, 136, 0.05) !important;
                    --ag-foreground-color: #ffffff !important;
                    --ag-header-foreground-color: #00cec9 !important;
                    --ag-border-color: rgba(255, 255, 255, 0.08) !important;
                    --ag-secondary-foreground-color: #b2bec3 !important;
                    
                    border-radius: 12px;
                    overflow: hidden;
                    border: 1px solid rgba(255, 255, 255, 0.08);
                }
                .ag-header-cell-text {
                    color: #00cec9 !important;
                    font-weight: 700 !important;
                }
            `}</style>

            <div className="network-scan-container">
                <h1 className="scan-title">Network Scanner</h1>
                
                <div className="scan-card">
                    <Form onSubmit={handleScan}>
                        <Row className="align-items-end">
                            <Col md={5} sm={12} className="mb-3 mb-md-0">
                                <Form.Group controlId="subnet">
                                    <Form.Label style={{ color: '#b2bec3', fontWeight: 600 }}>Subnet / IP Address</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        placeholder="e.g. 192.168.1.0 or 172.30.140.254" 
                                        value={subnet}
                                        onChange={(e) => setSubnet(e.target.value)}
                                        style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            color: '#ffffff'
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3} sm={12} className="mb-3 mb-md-0">
                                <Form.Group controlId="mask">
                                    <Form.Label style={{ color: '#b2bec3', fontWeight: 600 }}>Mask (CIDR)</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        placeholder="e.g. 24 (leave blank for single IP)" 
                                        value={mask}
                                        onChange={(e) => setMask(e.target.value)}
                                        style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            color: '#ffffff'
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4} sm={12} className="text-md-end text-start">
                                {isLoading && (
                                    <Button 
                                        variant="danger" 
                                        className="me-2"
                                        onClick={handleStopScan}
                                        disabled={isStopping}
                                        style={{ fontWeight: 600 }}
                                    >
                                        {isStopping ? 'Stopping...' : 'Stop Scan'}
                                    </Button>
                                )}
                                <Button 
                                    type="submit" 
                                    variant="primary" 
                                    className="me-2"
                                    disabled={isLoading}
                                    style={{
                                        background: 'var(--btn-primary-bg)',
                                        borderColor: 'var(--btn-primary-border)',
                                        fontWeight: 600
                                    }}
                                >
                                    {isLoading ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Scanning...
                                        </>
                                    ) : 'Start Scan'}
                                </Button>
                                {hosts.length > 0 && (
                                    <Button 
                                        variant="secondary" 
                                        onClick={onExportClick}
                                        style={{ fontWeight: 600 }}
                                    >
                                        Export
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </Form>
                </div>

                {hosts.length > 0 ? (
                    <Row>
                        <Col>
                            <h3 className="results-header">
                                Active Devices Found ({hosts.length})
                            </h3>
                            <div className="ag-theme-alpine" style={{ height: '400px', width: '100%' }}>
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={hosts}
                                    columnDefs={columnDefs}
                                    rowSelection="single"
                                    onGridReady={onGridReady}
                                    suppressExcelExport={true}
                                />
                            </div>
                        </Col>
                    </Row>
                ) : (
                    !isLoading && (
                        <div className="text-center py-5" style={{ background: 'rgba(30, 39, 46, 0.2)', borderRadius: '12px', border: '1px dashed rgba(255, 255, 255, 0.1)' }}>
                            <p style={{ color: '#b2bec3', margin: 0 }}>Enter a subnet/IP and start scanning to discover hosts.</p>
                        </div>
                    )
                )}
            </div>

            {/* Modal to pick target site for adding device */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
                <Modal.Header closeButton style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                    <Modal.Title style={{ color: '#ffffff' }}>Add Discovered Device to Site</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: '#dfe6e9' }}>
                        You are adding <strong>{hostToAdd?.hostname || hostToAdd?.ip}</strong> ({hostToAdd?.ip}) to your system.
                    </p>
                    <Form.Group controlId="targetSiteSelect" className="mt-3">
                        <Form.Label style={{ color: '#b2bec3', fontWeight: 600 }}>Target Site</Form.Label>
                        {isSitesLoading ? (
                            <div><Spinner animation="border" size="sm" /> Loading Sites...</div>
                        ) : (
                            <Form.Select
                                value={targetSiteId}
                                onChange={(e) => setTargetSiteId(e.target.value)}
                                style={{
                                    backgroundColor: 'rgba(30, 39, 46, 0.95)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    color: '#ffffff'
                                }}
                            >
                                <option value="">-- Choose a Site --</option>
                                {sites?.map(site => (
                                    <option key={site._id} value={site._id}>
                                        {site.company} - {site.name}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                    <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="success" onClick={handleConfirmAddDevice} disabled={!targetSiteId || isAddingDevice}>
                        Confirm & Add
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default NetworkScanScreen;
